import api, { BASE_URL, refreshSessionCookies } from "./api";
import {
  getBrowserCompatibleFetchOptions,
  handleAuthError,
} from "../utils/browserUtils";

const AuthService = {
  checkAuthStatus: async () => {
    try {
      const statusJwt = async () => {
        const jwtResponse = await fetch(`${BASE_URL}/auth/status-jwt`, {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
          credentials: "include",
        });
        if (!jwtResponse.ok) {
          return { ok: false, authenticated: false, body: null };
        }
        const body = await jwtResponse.json();
        if (body?.isAuthenticated && body.user) {
          return { ok: true, authenticated: true, data: body };
        }
        return { ok: true, authenticated: false, body };
      };

      let result = await statusJwt();
      if (result.authenticated) return result.data;

      let hints = result.body || {};

      if (hints.hasRefreshTokenCookie) {
        if (await refreshSessionCookies()) {
          result = await statusJwt();
          if (result.authenticated) return result.data;
          hints = result.body || hints;
        }
      }

      if (hints.hasPassportSession) {
        try {
          const issued = await fetch(`${BASE_URL}/auth/session-jwt`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
          });
          if (issued.ok) {
            result = await statusJwt();
            if (result.authenticated) return result.data;
          }
        } catch {
          /* fall through to /auth/status */
        }
      }

      const fullUrl = `${BASE_URL}/auth/status`;
      const fetchOptions = getBrowserCompatibleFetchOptions({
        method: "GET",
      });

      const response = await fetch(fullUrl, fetchOptions);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      const isNetworkError =
        error.name === "TypeError" &&
        (error.message.includes("Failed to fetch") ||
          error.message.includes("NetworkError"));

      const isCorsError =
        error.message.includes("CORS") ||
        error.message.includes("cross-origin") ||
        error.message.includes("credentials");

      if (isNetworkError && process.env.NODE_ENV === "production") {
        return { isAuthenticated: false, user: null };
      }

      if (isNetworkError || isCorsError) {
        try {
          const fallbackResponse = await fetch(`${BASE_URL}/auth/status`, {
            method: "GET",
            credentials: "include",
            mode: "cors",
            headers: {
              Accept: "application/json",
            },
          });

          if (!fallbackResponse.ok) {
            throw new Error(
              `Fallback HTTP error! status: ${fallbackResponse.status}`
            );
          }

          return await fallbackResponse.json();
        } catch {
          if (process.env.NODE_ENV === "production") {
            return { isAuthenticated: false, user: null };
          }

          try {
            await fetch(`${BASE_URL}/auth/status`, {
              method: "GET",
              mode: "no-cors",
            });
            return { isAuthenticated: false, user: null };
          } catch {
            throw handleAuthError(error, "auth status check");
          }
        }
      } else {
        throw handleAuthError(error, "auth status check");
      }
    }
  },

  initiateGoogleLogin: () => {
    sessionStorage.setItem("googleOAuthInitiated", Date.now().toString());
    window.location.href = `${BASE_URL}/auth/google?client=portal`;
  },

  loginWithEmailPassword: async (email, password) => {
    try {
      const response = await fetch(
        `${BASE_URL}/auth/login`,
        getBrowserCompatibleFetchOptions({
          method: "POST",
          body: JSON.stringify({ email, password }),
        })
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message || `HTTP error! status: ${response.status}`
        );
      }

      return data;
    } catch (error) {
      throw handleAuthError(error, "email/password login");
    }
  },

  forgotPassword: async (email) => {
    const response = await fetch(`${BASE_URL}/auth/forgot-password`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        data.message || `HTTP error! status: ${response.status}`
      );
    }

    return data;
  },

  resetPassword: async (token, password) => {
    const response = await fetch(`${BASE_URL}/auth/reset-password`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ token, password }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        data.message || `HTTP error! status: ${response.status}`
      );
    }

    return data;
  },

  validateResetToken: async (token) => {
    const response = await fetch(`${BASE_URL}/auth/validate-reset-token`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ token }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        data.message || `HTTP error! status: ${response.status}`
      );
    }

    return data;
  },

  logout: async () => {
    const response = await fetch(`${BASE_URL}/auth/logout`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  },

  getUserProfile: async () => {
    const response = await api.get("/users/profile");
    return response.data;
  },

  registerWithToken: async (registrationData) => {
    const response = await fetch(`${BASE_URL}/auth/register`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(registrationData),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        data.message || `HTTP error! status: ${response.status}`
      );
    }

    return data;
  },

  registerOwner: async (ownerData) => {
    const response = await fetch(`${BASE_URL}/auth/register-owner`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(ownerData),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        data.message || `HTTP error! status: ${response.status}`
      );
    }

    return data;
  },

  updatePassword: async (currentPassword, newPassword) => {
    const response = await api.put("/users/password", {
      currentPassword,
      newPassword,
    });
    return response.data;
  },
};

export default AuthService;
